import React, { useEffect, useRef, useState } from "react"
import CompanySelector from "ATSComponents/Common/CompanySelector"
import axios from "axios"
import Select, { components } from "react-select"
import moment from "moment"
import Swal from "sweetalert2"
import { Calendar, momentLocalizer } from "react-big-calendar"
import "react-big-calendar/lib/css/react-big-calendar.css"
import { Modal, Table, Button, Spinner } from "react-bootstrap"
import { FaArchive, FaCalendarDay, FaCalendarPlus, FaChevronLeft, FaChevronRight, FaClock, FaCopy, FaDownload, FaEdit, FaInfoCircle, FaListUl, FaRedo, FaSearch, FaShareAlt, FaTrash, FaVideo } from "react-icons/fa"
import { getCandidateNames } from "./calendarCandidateUtils"
import ScheduleInterviewDialog from "ATSComponents/Common/ScheduleInterviewDialog"
import { getCalendarApiErrorMessage } from "./calendarError"

const toastSuccess = title =>
  Swal.fire({
    icon: "success",
    title,
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 1600,
    timerProgressBar: true,
  })

const toastError = title =>
  Swal.fire({
    icon: "error",
    title,
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 2000,
    timerProgressBar: true,
  })

const JOB_PAGE_SIZE = 10

const EmptyInterviewState = ({ compact = false, message = "No interviews scheduled", description = "Try changing the date or company filters, or schedule a new interview to see it here." }) => (
  <div
    style={{
      minHeight: compact ? 150 : 205,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: compact ? "24px 16px" : "34px 20px",
      background: "linear-gradient(180deg, #fbfdff 0%, #f4f9fe 100%)",
    }}
  >
    <div style={{ textAlign: "center", maxWidth: 380 }}>
      <div
        aria-hidden="true"
        style={{
          width: 52,
          height: 52,
          margin: "0 auto 12px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "50%",
          background: "#eaf4ff",
          border: "1px solid #cfe3f7",
          color: "#1a5fa8",
          fontSize: 22,
        }}
      >
        <FaCalendarDay />
      </div>
      <div style={{ color: "#18324d", fontSize: 16, fontWeight: 700, lineHeight: 1.3 }}>
        {message}
      </div>
      <div style={{ marginTop: 6, color: "#6b7f93", fontSize: 12.5, lineHeight: 1.5 }}>
        {description}
      </div>
    </div>
  </div>
)

const isAllCalendarCompany = companyId => String(companyId || "").toLowerCase() === "all"
const blockAllCompanyMutation = () => {
  Swal.fire({
    icon: "warning",
    title: "Select a company",
    text: "Please select a specific company before editing, archiving, or deleting a calendar event.",
    confirmButtonText: "OK",
  })
}

const jobSelectStyles = {
  control: base => ({
    ...base,
    minHeight: 38,
    borderColor: "#1a5fa8",
    boxShadow: "none",
    fontSize: 13,
    "&:hover": { borderColor: "#1a5fa8" },
  }),
  valueContainer: base => ({ ...base, padding: "2px 10px" }),
  menu: base => ({ ...base, zIndex: 10, width: 260, minWidth: 260 }),
  option: (base, state) => ({
    ...base,
    fontSize: 13,
    backgroundColor: state.isSelected ? "#2f80ed" : state.isFocused ? "#eaf4ff" : "#fff",
    color: state.isSelected ? "#fff" : "#1f2937",
  }),
}

function MyCalendar() {
  const [events, setEvents] = useState([])
  const [companyId, setCompanyId] = useState(null)
  const [showTodayModal, setShowTodayModal] = useState(false)
  const [modalDayEvents, setModalDayEvents] = useState([])
  const [loading, setLoading] = useState(false)
  const [calendarScope, setCalendarScope] = useState("select")
  const [displayMode, setDisplayMode] = useState("calendar")
  const [selectedFilterDate, setSelectedFilterDate] = useState("")
  const [calendarDate, setCalendarDate] = useState(new Date())
  const [calendarView, setCalendarView] = useState("month")
  const [scrollToEventTime, setScrollToEventTime] = useState(new Date(1970, 0, 1, 9, 0, 0))
  const [sortConfig, setSortConfig] = useState({ key: "date_time", direction: "asc" })
  const [showArchiveModal, setShowArchiveModal] = useState(false)
  const [archiveReason, setArchiveReason] = useState("")
  const [archiveReasonErr, setArchiveReasonErr] = useState("")
  const [archiveSaving, setArchiveSaving] = useState(false)
  const [calendarIdToArchive, setCalendarIdToArchive] = useState(null)
  const [deletingEventId, setDeletingEventId] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deletionReason, setDeletionReason] = useState("")
  const [deletionReasonErr, setDeletionReasonErr] = useState("")
  const [showEditModal, setShowEditModal] = useState(false)
  const [editLoading, setEditLoading] = useState(false)
  const [editSaving, setEditSaving] = useState(false)
  const [editTimeError, setEditTimeError] = useState("")
  const [originalDateTime, setOriginalDateTime] = useState(null)
  const [selectedInterview, setSelectedInterview] = useState(null)
  const [scheduleHistory, setScheduleHistory] = useState([])
  const [scheduleHistoryLoading, setScheduleHistoryLoading] = useState(false)
  const [scheduleHistoryError, setScheduleHistoryError] = useState("")
  const [showShareModal, setShowShareModal] = useState(false)
  const [selectedEventForShare, setSelectedEventForShare] = useState(null)
  const [shareCompanyId, setShareCompanyId] = useState(null)
  const [shareRecruiters, setShareRecruiters] = useState([])
  const [shareCandidates, setShareCandidates] = useState([])
  const [shareRecruiterOptions, setShareRecruiterOptions] = useState([])
  const [shareRecruiterSearch, setShareRecruiterSearch] = useState("")
  const [shareRecruitersLoading, setShareRecruitersLoading] = useState(false)
  const [isSharing, setIsSharing] = useState(false)
  const [jobOptions, setJobOptions] = useState([])
  const [selectedJob, setSelectedJob] = useState(null)
  const [recruiterOptions, setRecruiterOptions] = useState([])
  const [selectedRecruiter, setSelectedRecruiter] = useState(null)
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [calendarPage, setCalendarPage] = useState(1)
  const [exporting, setExporting] = useState(false)
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [dateRangeError, setDateRangeError] = useState("")
  const [jobLoading, setJobLoading] = useState(false)
  const [jobPage, setJobPage] = useState(1)
  const [jobTotalPages, setJobTotalPages] = useState(1)
  const [jobSearch, setJobSearch] = useState("")
  const [scheduleInterviewOpen, setScheduleInterviewOpen] = useState(false)
  const [scheduleInterviewDate, setScheduleInterviewDate] = useState(null)
  const [summaryNow, setSummaryNow] = useState(() => new Date())
  const calendarDateInputRef = useRef(null)
  const localizer = momentLocalizer(moment)
  const [modalTitle, setModalTitle] = useState("Interviews")
  const handleCompanyChange = company => {
    if (company?.company_id) {
      setCompanyId(company.company_id)
      setSelectedJob(null)
      setSelectedRecruiter(null)
      setSearch("")
      setDebouncedSearch("")
      setCalendarPage(1)
      setJobSearch("")
      setJobPage(1)
    }
  }
  const resetFilters = () => {
    setSelectedJob(null)
    setSelectedRecruiter(null)
    setStartDate("")
    setEndDate("")
    setDateRangeError("")
    setSelectedFilterDate("")
    setCalendarScope("select")
    setSearch("")
    setDebouncedSearch("")
    setCalendarPage(1)
    setJobSearch("")
    setJobPage(1)
  }
  const userInfos = JSON.parse(sessionStorage.getItem("user-info") || "{}")
  const user_id = userInfos?.user_details?.user_id
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim())
      setCalendarPage(1)
    }, 300)
    return () => window.clearTimeout(timer)
  }, [search])

  useEffect(() => {
    if (companyId) fetchEvents()
  }, [companyId, calendarScope, selectedFilterDate, selectedJob, selectedRecruiter, startDate, endDate, debouncedSearch, calendarPage])

  useEffect(() => {
    if (!companyId) return
    const fetchRecruiters = async () => {
      try {
        const response = await axios.get("/api/v1/ats-users/")
        const list = response.data?.users || []
        setRecruiterOptions(
          list.map(recruiter => ({
            value: recruiter.id,
            label:
              [recruiter.first_name, recruiter.middle_name, recruiter.last_name]
                .filter(Boolean)
                .join(" ") || recruiter.email || `Recruiter ${recruiter.id}`,
          })).filter(recruiter => recruiter.value != null)
        )
      } catch (error) {
        setRecruiterOptions([])
      }
    }
    fetchRecruiters()
  }, [companyId])

  useEffect(() => {
    if (!companyId) return
    fetchJobOptions(1, "")
  }, [companyId])

  useEffect(() => {
    const intervalId = window.setInterval(() => setSummaryNow(new Date()), 60000)
    return () => window.clearInterval(intervalId)
  }, [])

  useEffect(() => {
    if (!showShareModal || !shareCompanyId) return
    const fetchRecruiters = async () => {
      setShareRecruitersLoading(true)
      try {
        const response = await axios.get(`/api/v1/get-all-recruiters/?company_id=${shareCompanyId}`)
        const data = response.data?.results || response.data || []
        setShareRecruiterOptions(
          data.map(contact => ({
            id: contact.id,
            name: [contact.first_name, contact.middle_name, contact.last_name].filter(Boolean).join(" ") || contact.email || `User ${contact.id}`,
          }))
        )
      } catch (error) {
        setShareRecruiterOptions([])
      } finally {
        setShareRecruitersLoading(false)
      }
    }
    fetchRecruiters()
  }, [showShareModal, shareCompanyId])

  const fetchJobOptions = async (pageNumber = 1, search = jobSearch) => {
    if (!companyId) return

    try {
      setJobLoading(true)
      const response = await axios.get("/api/v1/create-job-order/", {
        params: {
          company_id: companyId === "All" ? "all" : companyId,
          page: pageNumber,
          size: JOB_PAGE_SIZE,
          ...(search ? { search } : {}),
        },
      })
      const list = response.data?.results || response.data?.data || response.data || []
      const totalCount = response.data?.count || list.length
      const mapped = list
        .map(job => ({
          value: job.job_id ?? job.id ?? job.job_order_id,
          label: job.job_title || job.title || `Job ${job.job_id ?? job.id}`,
        }))
        .filter(job => job.value)
      setJobOptions(mapped)
      setJobPage(pageNumber)
      setJobTotalPages(Math.max(1, Math.ceil(totalCount / JOB_PAGE_SIZE)))
    } catch (error) {
      console.error("Error fetching job orders", error)
      setJobOptions([])
      setJobTotalPages(1)
    } finally {
      setJobLoading(false)
    }
  }

  const JobMenuList = props => (
    <components.MenuList {...props}>
      {props.children}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          padding: "8px 10px",
          borderTop: "1px solid #e2e8f0",
          background: "#fff",
          fontSize: 12,
        }}
      >
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          disabled={jobLoading || jobPage <= 1}
          onMouseDown={event => {
            event.preventDefault()
            fetchJobOptions(jobPage - 1, jobSearch)
          }}
        >
          Prev
        </button>
        <span style={{ color: "#64748b" }}>Page {jobPage}/{jobTotalPages}</span>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          disabled={jobLoading || jobPage >= jobTotalPages}
          onMouseDown={event => {
            event.preventDefault()
            fetchJobOptions(jobPage + 1, jobSearch)
          }}
        >
          Next
        </button>
      </div>
    </components.MenuList>
  )

  const buildEventQueryParams = ({ exportExcel = false } = {}) => {
    const params = {
      company_id: companyId === "All" ? "all" : companyId,
      ...(calendarScope === "all" ? { page: calendarPage, size: 1000 } : {}),
      ...(calendarScope === "select" ? { today_events: true } : {}),
      ...(calendarScope === "archive" ? { archived_events: true } : {}),
      ...(calendarScope === "my" ? { user_id } : {}),
      ...(selectedFilterDate && !startDate ? { start_date: selectedFilterDate } : {}),
      ...(selectedRecruiter?.value ? { recruiter_id: selectedRecruiter.value } : {}),
      ...(startDate ? { start_date: startDate } : {}),
      ...(endDate ? { end_date: endDate } : {}),
      ...(selectedJob?.value ? { job_id: selectedJob.value } : {}),
      ...(debouncedSearch.trim() ? { search: debouncedSearch.trim(), page: 1 } : {}),
      ...(exportExcel ? { export: "excel" } : {}),
    }
    return params
  }

  const fetchEvents = async () => {
    if (startDate && endDate && endDate < startDate) {
      setDateRangeError("End date cannot be earlier than the start date.")
      return
    }
    setDateRangeError("")
    setLoading(true)
    try {
      const response = await axios.get("/api/v1/events/", {
        params: buildEventQueryParams(),
      })
      const grouped = {}
      const eventRows = Array.isArray(response.data?.results) ? response.data.results : []
      eventRows.forEach(event => {
        const data = event.calendit_response?.results?.data || []
        const first = data[0]
        const selectedDate = first?.selected_date || event.start_date || event.event_time?.split("T")?.[0]
        const selectedTime = first?.selected_time || event.start_time || event.event_time?.split("T")?.[1]?.slice(0, 8)
        if (!selectedDate) return
        const key = `${selectedDate}T${selectedTime || "00:00:00"}`
        if (!grouped[key]) grouped[key] = []
        grouped[key].push({
          calendar_id: event.calendar_id,
          candidate: resolveCandidateName(event),
          candidate_ids: event.candidate_ids,
          contact_ids: event.contact_ids,
          job_ids: event.job_ids,
          job: Array.isArray(event.job_ids) && event.job_ids.length
            ? event.job_ids.map(job => job.name).join(", ")
            : "-",
          // recruiter: event.contact_ids[0].name || "-",
          recruiter: Array.isArray(event.contact_ids) && event.contact_ids.length
            ? event.contact_ids.map(c => c.name).join(", ")
            : "-",
          created_by_name: event.created_by_name || event.created_by?.name || "-",
          created_at: event.created_at || null,
          event_type: event.event_type || "-",
          event_priority: event.event_priority || "-",
          subject: event.subject || "-",
          description: event.description || "",
          start_date: event.start_date || selectedDate,
          end_date: event.end_date || selectedDate,
          subtype: event.event_sub_type || "-",
          status: event.status || "",
          reason_to_archive: event.reason_to_archive || "-",
          deletion_reason: event.deletion_reason || event.deleted_reason || "-",
          archived: event.archived === true,
          is_deleted: event.is_deleted === true,
          deleted: event.is_deleted === true,
          archived_by_name: event.archived_by_name || "—",
          deleted_by_name: event.deleted_by_name || "—",
          selected_date: selectedDate,
          selected_time: selectedTime || "00:00:00",
          end_time: first?.end_time || event.end_time || null,
          time_zone: event.time_zone || "Asia/Kolkata",
          duration: event.duration || "30 Minutes",
          meeting_link: first?.meeting_link || event.meeting_link || null,
          google_calendar_link: first?.google_calendar_link || event.google_calendar_link || null,
          participant_link: first?.participant_link || event.participant_link || null,
        })
      })
      const apiEvents = Object.keys(grouped).map(key => {
        const group = grouped[key]
        const first = group[0]
        const [year, month, day] = first.selected_date.split("-").map(Number)
        const [h = "0", m = "0", s = "0"] = (
          first.selected_time || "00:00:00"
        ).split(":")
        const start = new Date(
          year,
          month - 1,
          Number(day),
          Number(h),
          Number(m),
          Number(s)
        )
        const rawDuration = first.duration || "30"
        let durationMinutes = parseInt(
          String(rawDuration).match(/\d+/)?.[0] || "30",
          10
        )
        if (!Number.isFinite(durationMinutes) || durationMinutes <= 0)
          durationMinutes = 30
        return {
          title: group[0]?.subject || "Interview",
          start,
          end: new Date(start.getTime() + durationMinutes * 60000),
          allDay: false,
          fullData: group,
        }
      })
      setEvents(apiEvents)
    } catch (err) {
      console.error("Error fetching events", err)
      setEvents([])
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        (typeof err?.response?.data === "string" ? err.response.data : "Failed to load calendar events.")
      toastError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleExportExcel = async () => {
    if (!companyId) return
    if (startDate && endDate && endDate < startDate) {
      setDateRangeError("End date cannot be earlier than the start date.")
      return
    }
    setDateRangeError("")
    setExporting(true)
    try {
      const response = await axios.get("/api/v1/events/", {
        params: buildEventQueryParams({ exportExcel: true }),
        responseType: "blob",
      })

      const sourceBlob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: "text/csv;charset=utf-8" })
      const csvText = await sourceBlob.text()
      const firstLineBreak = csvText.search(/[\r\n]/)
      const headerEnd = firstLineBreak === -1 ? csvText.length : firstLineBreak
      const headerWithBom = csvText.slice(0, headerEnd)
      const bom = headerWithBom.startsWith("\uFEFF") ? "\uFEFF" : ""
      const header = headerWithBom.slice(bom.length)
        .replace(/(^|,)(\s*)"Interview Name"(?=,|$)/g, '$1$2"Subject"')
        .replace(/(^|,)(\s*)Interview Name(?=,|$)/g, "$1$2Subject")
      const blob = new Blob([`${bom}${header}${csvText.slice(headerEnd)}`], { type: "text/csv;charset=utf-8" })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = "interview-calendar-report.csv"
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to export the interview calendar."
      toastError(message)
    } finally {
      setExporting(false)
    }
  }
  const handleSelectEvent = event => {
    if (event?.fullData) {
      setModalDayEvents(event.fullData)
      setShowTodayModal(true)
    } else {
      setModalDayEvents([event])
      setShowTodayModal(true)
    }
  }

  const handleSelectCalendarSlot = slotInfo => {
    if (!companyId) {
      toastError("Please select company first")
      return
    }
    const clickedDate = slotInfo?.start ? new Date(slotInfo.start) : new Date()
    setScheduleInterviewDate(clickedDate)
    // Do not open the schedule dialog on calendar slot selection
  }
  const formatDateDirect = dateString => {
    if (!dateString) return "-"
    const [year, month, day] = String(dateString).split("T")[0].split("-")
    if (!year || !month || !day) return "-"
    return `${day}-${month}-${year}`
  }

  const formatCreatedAt = value => {
    if (!value) return "--"
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return "--"
    const parts = new Intl.DateTimeFormat(undefined, {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    }).formatToParts(date)
    const get = type => parts.find(part => part.type === type)?.value || ""
    return `${get("day")}-${get("month")}-${get("year")} ${get("hour")}:${get("minute")} ${get("dayPeriod")}`
  }

  const resolveCandidateName = (event = {}, fallback = "-") => {
    const candidateNames = getCandidateNames(event.candidate_ids || event.candidate_id)
    if (candidateNames && candidateNames !== "-") return candidateNames

    if (Array.isArray(event.candidate_names) && event.candidate_names.length) {
      return event.candidate_names.filter(Boolean).join(", ")
    }

    const directName =
      event.candidate_name ||
      event.candidate ||
      event.name ||
      [event.first_name, event.middle_name, event.last_name].filter(Boolean).join(" ")

    if (directName && directName !== "-") return directName
    return fallback || "-"
  }

  const formatTimeDirect = timeString => {
    if (!timeString || typeof timeString !== "string") return "-"
    const [hours, minutes] = timeString.split(":")
    if (Number.isNaN(Number(hours)) || Number.isNaN(Number(minutes))) return "-"
    const hourNumber = Number(hours)
    const suffix = hourNumber >= 12 ? "PM" : "AM"
    const displayHour = hourNumber % 12 || 12
    return `${displayHour}:${String(minutes).padStart(2, "0")} ${suffix}`
  }
  const toApiTimeValue = timeValue => {
    if (!timeValue || typeof timeValue !== "string") return null
    const [hours, minutes] = timeValue.split(":")
    if (Number.isNaN(Number(hours)) || Number.isNaN(Number(minutes))) return null
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`
  }

  const getEventIdList = items =>
    (Array.isArray(items) ? items : items ? [items] : [])
      .map(item => (typeof item === "object" ? item.id ?? item.candidate_id ?? item.contact_id ?? item.job_id : item))
      .filter(Boolean)

  const buildInterviewPayload = (event = {}) => ({
    event_type: event.event_type || "Interview",
    event_sub_type: event.event_sub_type || event.subtype || "Video",
    candidate_id: getEventIdList(event.candidate_ids || event.candidate_id),
    contact_id: getEventIdList(event.contact_ids || event.contact_id),
    job_id: getEventIdList(event.job_ids || event.job_id),
    subject: event.subject || "Interview",
    description: event.description || "",
    start_date: event.start_date || event.selected_date || null,
    end_date: event.end_date || event.start_date || event.selected_date || null,
    start_time: toApiTimeValue(event.start_time || event.selected_time) || event.start_time || event.selected_time || null,
    end_time: toApiTimeValue(event.end_time) || event.end_time || null,
    time_zone: event.time_zone || event.timezone || "Asia/Kolkata",
    candidate_timezone: event.candidate_timezone || event.candidateTimeZone || "Asia/Kolkata",
    recruiter_timezone: event.recruiter_timezone || event.recruiterTimeZone || "Asia/Kolkata",
    panel_timezones: event.panel_timezones || event.panelTimezones || {},
  })

  // Validates the edit modal's Start Time / End Time against Start Date
  const getEditTimeError = interview => {
    const dateValue = interview?.selected_date || interview?.start_date
    const startTime = toApiTimeValue(interview?.selected_time || interview?.start_time)
    const endTime = toApiTimeValue(interview?.end_time)

    if (!startTime) return "Please select a start time"
    if (!endTime) return "Please select an end time"
    if (dateValue && endTime <= startTime) return "End time must be after start time"
    return ""
  }

  const snapshotDateTime = interview => ({
    date: interview?.selected_date || interview?.start_date || "",
    startTime: String(interview?.selected_time || interview?.start_time || "").slice(0, 5),
    endTime: String(interview?.end_time || "").slice(0, 5),
  })

  const hasDateTimeChanged = interview => {
    if (!originalDateTime) return false
    const current = snapshotDateTime(interview)
    return (
      current.date !== originalDateTime.date ||
      current.startTime !== originalDateTime.startTime ||
      current.endTime !== originalDateTime.endTime
    )
  }

  const handleOpenCalendarDatePicker = () => {
    const input = calendarDateInputRef.current
    if (!input) return
    if (typeof input.showPicker === "function") {
      input.showPicker()
      return
    }
    input.click()
  }

  const handleCalendarDateChange = e => {
    const value = e.target.value
    setSelectedFilterDate(value)
    if (value) {
      const [year, month, day] = value.split("-").map(Number)
      setCalendarDate(new Date(year, month - 1, day))
    }
  }

  const loadScheduleHistory = async (calendarId, fallbackId = null) => {
    setScheduleHistoryLoading(true)
    setScheduleHistoryError("")
    try {
      const response = await axios.get(`/api/v1/events/${calendarId}/history/`)
      const records = Array.isArray(response.data?.results)
        ? response.data.results
        : []
      if (records.length || !fallbackId || String(fallbackId) === String(calendarId)) {
        setScheduleHistory(records.sort((a, b) => new Date(b.changed_at || b.created_at || 0) - new Date(a.changed_at || a.created_at || 0)))
        return
      }
      const fallbackResponse = await axios.get(`/api/v1/events/${fallbackId}/history/`)
      const fallbackRecords = Array.isArray(fallbackResponse.data?.results)
        ? fallbackResponse.data.results
        : []
      setScheduleHistory(fallbackRecords.sort((a, b) => new Date(b.changed_at || b.created_at || 0) - new Date(a.changed_at || a.created_at || 0)))
    } catch {
      setScheduleHistoryError("Unable to load schedule history.")
    } finally { setScheduleHistoryLoading(false) }
  }

  const getHistoryValue = value => {
    if (Array.isArray(value)) {
      const first = value[0]
      return first?.created_events?.[0] || first || {}
    }
    return value || {}
  }

  const formatHistoryDateTime = value => {
    const item = getHistoryValue(value)
    const date = item.start_date || item.selected_date || item.end_date
    const start = item.start_time || item.selected_time
    const end = item.end_time
    if (!date && !start && !end) return "--"
    const formatPart = time => {
      if (!date || !time) return time || "--"
      const parsed = moment(`${date} ${time}`)
      return parsed.isValid() ? parsed.format("MMM DD, YYYY, hh:mm A") : `${date} ${time}`
    }
    const formattedStart = formatPart(start)
    const formattedEnd = end && date ? moment(`${date} ${end}`).format("hh:mm A") : null
    return formattedEnd ? `${formattedStart} – ${formattedEnd}` : formattedStart
  }

  const formatHistoryTimestamp = value => {
    if (!value) return "--"
    const parsed = moment(value)
    return parsed.isValid() ? parsed.format("MMM DD, YYYY, hh:mm A") : "--"
  }

  const formatHistoryFieldValue = (key, value) => {
    if (value === null || value === undefined || value === "") return "--"
    if (["created_at", "updated_at", "event_time"].includes(key)) return formatHistoryTimestamp(value)
    if (["start_date", "end_date"].includes(key)) {
      const parsed = moment(value)
      return parsed.isValid() ? parsed.format("MMM DD, YYYY") : String(value)
    }
    if (["start_time", "end_time"].includes(key)) {
      const parsed = moment(String(value), ["HH:mm:ss", "HH:mm"])
      return parsed.isValid() ? parsed.format("hh:mm A") : String(value)
    }
    if (typeof value === "object") return JSON.stringify(value)
    return String(value)
  }

  const getPanelTimezoneName = (names, userId) => {
    let resolvedNames = names
    if (typeof resolvedNames === "string") {
      try { resolvedNames = JSON.parse(resolvedNames) } catch { resolvedNames = null }
    }
    if (!resolvedNames || typeof resolvedNames !== "object") return "Panel member"
    return resolvedNames[String(userId)] || resolvedNames[userId] || "Panel member"
  }

  const getPanelTimezoneNames = value => {
    if (typeof value === "string") {
      try { return JSON.parse(value) || {} } catch { return {} }
    }
    return value && typeof value === "object" ? value : {}
  }

  const getHistoryDetailRows = (before, after) => {
    const previous = getHistoryValue(before)
    const next = getHistoryValue(after)
    const labels = {
      candidate_id_name: "Candidate",
      job_id_name: "Job",
      company_name: "Company",
      contact_id_name: "Panel / contact",
      event_type: "Interview type",
      event_sub_type: "Interview subtype",
      start_date: "Start date",
      start_time: "Start time",
      end_date: "End date",
      end_time: "End time",
      duration: "Duration",
      time_zone: "Time zone",
      subject: "Subject",
      description: "Description",
      meeting_link: "Meeting link",
      participant_link: "Participant link",
      reminder: "Reminder",
      recurring: "Recurring",
      recurrence_pattern: "Recurrence",
      status: "Status",
      event_priority: "Priority",
      created_by_name: "Created by",
      panel_timezone_names: "Panel timezone owners",
      panel_timezones: "Panel timezones",
    }
    const keys = [...new Set([...Object.keys(previous), ...Object.keys(next)])]
      .filter(key => !/(^|_)(id|ids)$/.test(key) && !["id", "job_id", "candidate_id", "contact_id", "company", "calendar_id", "meeting_id", "created_by", "updated_by", "panel_timezone_names", "parent_event", "auto_followup", "reminder_note", "job_name", "recurrence", "recurrence_pattern", "google_calendar_link", "candidate", "candidate_id_name", "repeat", "private"].includes(key))
    return keys
      .map(key => {
        if (key === "panel_timezones") {
          const formatPanelTimezones = (zones, names) => Object.entries(zones || {})
            .map(([userId, timezone]) => `${getPanelTimezoneName(names, userId)}: ${timezone}`)
            .join("\n") || "--"
          return { label: labels[key], before: formatPanelTimezones(previous[key], previous.panel_timezone_names), after: formatPanelTimezones(next[key], next.panel_timezone_names) }
        }
        return { label: labels[key] || key.replace(/_/g, " "), before: formatHistoryFieldValue(key, previous[key]), after: formatHistoryFieldValue(key, next[key]) }
      })
      .filter(row => row.before !== undefined || row.after !== undefined)
  }

  const handleEditInterview = ev => {
    if (isAllCalendarCompany(companyId)) return blockAllCompanyMutation()
    if (!ev?.calendar_id) return
    setShowTodayModal(false)
    setSelectedInterview(ev)
    setEditTimeError(getEditTimeError(ev))
    setOriginalDateTime(snapshotDateTime(ev))
    setShowEditModal(true)
    setScheduleHistory([])
    setScheduleHistoryError("")
    loadScheduleHistory(ev.calendar_id)
    setEditLoading(true)
    axios
      .get(`/api/v1/events/${ev.calendar_id}/`, { params: { company_id: companyId === "All" ? "all" : companyId } })
      .then(res => {
        const event = res.data || {}
        const calendit = event.calendit_response?.results?.data?.[0] || {}
        const nextInterview = {
          ...ev,
          ...event,
          candidate: resolveCandidateName(event, ev.candidate),
          recruiter: Array.isArray(event.contact_ids) && event.contact_ids.length
            ? event.contact_ids.map(contact => contact.name).join(", ")
            : ev.recruiter,
          job: Array.isArray(event.job_ids) && event.job_ids.length
            ? event.job_ids.map(job => job.name).join(", ")
            : ev.job,
          event_type: event.event_type || ev.event_type,
          created_at: event.created_at || ev.created_at || null,
          subtype: event.event_sub_type || ev.subtype,
          selected_date: calendit.selected_date || event.start_date || ev.selected_date,
          selected_time: calendit.selected_time || event.start_time || ev.selected_time,
          end_time: calendit.end_time || event.end_time || ev.end_time,
          meeting_link: calendit.meeting_link || ev.meeting_link,
          google_calendar_link: calendit.google_calendar_link || ev.google_calendar_link,
          description: event.description || ev.description || "",
        }
        setSelectedInterview(nextInterview)
        setEditTimeError(getEditTimeError(nextInterview))
        setOriginalDateTime(snapshotDateTime(nextInterview))
      })
      .catch(() => {
        setSelectedInterview(ev)
        setEditTimeError(getEditTimeError(ev))
        setOriginalDateTime(snapshotDateTime(ev))
      })
      .finally(() => setEditLoading(false))
  }

  const handleUpdateInterview = async () => {
    if (isAllCalendarCompany(companyId)) return blockAllCompanyMutation()
    if (!selectedInterview?.calendar_id) return
    const timeError = getEditTimeError(selectedInterview)
    if (timeError) {
      setEditTimeError(timeError)
      toastError(timeError)
      return
    }
    setEditSaving(true)
    try {
      const oldCalendarId = selectedInterview.calendar_id
      const response = await axios.put(
        `/api/v1/events/${selectedInterview.calendar_id}/`,
        buildInterviewPayload(selectedInterview),
        { params: { company_id: companyId === "All" ? "all" : companyId } }
      )
      const newCalendarId = response.data?.[0]?.created_events?.[0]?.calendar_id || oldCalendarId
      setSelectedInterview(current => ({ ...current, calendar_id: newCalendarId }))
      const detailsResponse = await axios.get(`/api/v1/events/${newCalendarId}/`, { params: { company_id: companyId === "All" ? "all" : companyId } })
      setSelectedInterview(current => ({ ...current, ...(detailsResponse.data || {}), calendar_id: newCalendarId }))
      await loadScheduleHistory(newCalendarId, oldCalendarId)
      setShowEditModal(false)
      fetchEvents()
      toastSuccess("Interview updated")
    } catch (error) {
      toastError(getCalendarApiErrorMessage(error, "Failed to update interview."))
    } finally {
      setEditSaving(false)
    }
  }

  const handleArchiveInterview = ev => {
    if (isAllCalendarCompany(companyId)) return blockAllCompanyMutation()
    if (!ev?.calendar_id) return
    setShowTodayModal(false)
    setCalendarIdToArchive(ev.calendar_id)
    setArchiveReason("")
    setArchiveReasonErr("")
    setShowArchiveModal(true)
  }

  const handleSaveArchive = async () => {
    if (isAllCalendarCompany(companyId)) return blockAllCompanyMutation()
    if (!archiveReason.trim()) {
      setArchiveReasonErr("Please provide a reason for archiving.")
      return
    }

    setArchiveSaving(true)
    setArchiveReasonErr("")
    try {
      await axios.post("/api/v1/events/archive/", {
        reason_to_archive: archiveReason,
        calendar_id: calendarIdToArchive,
      })
      setShowArchiveModal(false)
      setArchiveReason("")
      setCalendarIdToArchive(null)
      fetchEvents()
      toastSuccess("Interview archived")
    } catch (error) {
      const message = getCalendarApiErrorMessage(error, "Failed to archive calendar.")
      setArchiveReasonErr(message)
      toastError(message)
    } finally {
      setArchiveSaving(false)
    }
  }
  const handleDeleteInterview = async ev => {
    if (isAllCalendarCompany(companyId)) return blockAllCompanyMutation()
    if (!ev?.calendar_id) return
    setCalendarIdToArchive(ev.calendar_id)
    setDeletionReason("")
    setDeletionReasonErr("")
    setShowDeleteModal(true)
  }
  const handleConfirmDelete = async () => {
    if (isAllCalendarCompany(companyId)) return blockAllCompanyMutation()
    if (!deletionReason.trim()) {
      setDeletionReasonErr("Please provide a deletion reason.")
      return
    }
    setDeletingEventId(calendarIdToArchive)
    try {
      await axios.delete(
        `/api/v1/events/${calendarIdToArchive}/`,
        { data: { deletion_reason: deletionReason.trim() }, params: { company_id: companyId === "All" ? "all" : companyId } }
    )
    setShowDeleteModal(false)
    setDeletionReason("")
    setCalendarIdToArchive(null)
    fetchEvents()
    setShowTodayModal(false)
    toastSuccess("Interview archived")
    } catch (error) {
      const message = getCalendarApiErrorMessage(error, "Failed to delete interview.")
      setDeletionReasonErr(message)
      toastError(message)
    } finally {
      setDeletingEventId(null)
    }
  }

  const ActionButtons = ({ ev }) => {
    const hasCalendarLink = Boolean(ev.google_calendar_link)

    return (
      <div className="calendar-event-actions d-inline-flex flex-nowrap justify-content-center align-items-center gap-2">
        <button
          type="button"
          className="btn btn-sm btn-default border icons-btn edit"
          title="Edit"
          style={{ color: "#1a5fa8", background: "#eef6ff" }}
          onClick={() => handleEditInterview(ev)}
        >
          <FaEdit />
        </button>
        <button
          type="button"
          className="btn btn-sm btn-default border icons-btn archive"
          title="Archive"
          style={{ color: "#d9534f", background: "#fff1f1" }}
          onClick={() => handleArchiveInterview(ev)}
        >
          <FaArchive />
        </button>
        <button
          type="button"
          className="btn btn-sm btn-default border icons-btn delete"
          title="Delete"
          disabled={deletingEventId === ev.calendar_id}
          style={{
            color: "#b02a37",
            background: "#fdeceb",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => handleDeleteInterview(ev)}
        >
          {deletingEventId === ev.calendar_id ? (
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
              style={{ width: 14, height: 14, borderWidth: 2 }}
            />
          ) : (
            <FaTrash />
          )}
        </button>
      </div>
    )
  }

  const handleCopyMeetingLink = async link => {
    if (!link) return
    try {
      await navigator.clipboard.writeText(link)
      toastSuccess("Meeting link copied")
    } catch (error) {
      try {
        const tempInput = document.createElement("input")
        tempInput.value = link
        document.body.appendChild(tempInput)
        tempInput.select()
        document.execCommand("copy")
        document.body.removeChild(tempInput)
        toastSuccess("Meeting link copied")
      } catch (fallbackError) {
        toastError("Couldn't copy link, please copy manually")
      }
    }
  }

  const handleOpenShareModal = event => {
    setSelectedEventForShare(event)
    setShareRecruiters([])
    setShareRecruiterSearch("")
    setShareCandidates([])
    setShareRecruiterOptions([])
    setShareCompanyId(companyId === "All" ? null : companyId)
    setShowShareModal(true)
  }

  const handleShareCompanyChange = company => {
    if (company?.company_id && company.company_id !== shareCompanyId) {
      setShareCompanyId(company.company_id)
      setShareRecruiters([])
      setShareRecruiterSearch("")
    }
  }

  const handleSubmitShareMeeting = async () => {
    if (!selectedEventForShare) return
    if (!shareRecruiters.length && !shareCandidates.length) {
      Swal.fire({
        icon: "warning",
        title: "Select at least one recipient",
        text: "Please select at least one recruiter or candidate to share with.",
        confirmButtonText: "OK",
      })
      return
    }
    setIsSharing(true)
    try {
      await axios.post("/api/v1/meeting-invite/", {
        candidate_ids: shareCandidates,
        employee_ids: shareRecruiters,
        calendar_id: selectedEventForShare.calendar_id,
        company_id: shareCompanyId,
      })
      setShowShareModal(false)
      toastSuccess("Meeting link shared")
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: error?.message?.message || "Failed to share meeting link",
        text: "Please try again.",
        confirmButtonText: "OK",
      })
    } finally {
      setIsSharing(false)
    }
  }

  const MeetingLinkActions = ({ event, showDelete = false }) => {
    const link = event?.participant_link || event?.meeting_link
    const deleteButton = showDelete ? (
      <button
        type="button"
        className="btn btn-sm btn-danger"
        title="Delete interview"
        aria-label="Delete interview"
        disabled={deletingEventId === event?.calendar_id}
        onClick={() => handleDeleteInterview(event)}
        style={{ width: 32, height: 32, minWidth: 32, padding: 0, flex: "0 0 32px", display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}
      >
        {deletingEventId === event?.calendar_id ? (
          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" style={{ width: 14, height: 14, borderWidth: 2 }} />
        ) : <FaTrash style={{ fontSize: 12 }} />}
      </button>
    ) : null

    if (!link) return showDelete ? <div className="d-inline-flex align-items-center justify-content-center gap-1 w-100">N/A{deleteButton}</div> : "N/A"

    return (
      <div className="d-flex align-items-center justify-content-center gap-1 w-100" style={{ minHeight: 34 }}>
        <a
          href={link}
          target="_blank"
          rel="noreferrer"
          style={{
            color: "#fff",
            background: "#0d6efd",
            border: "1px solid #0d6efd",
            borderRadius: 6,
            width: 32,
            height: 32,
            padding: 0,
            boxSizing: "border-box",
            flex: "0 0 32px",
            fontWeight: 700,
            textDecoration: "none",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1,
          }}
          className="outlook-join-link"
          title="Join Meeting"
        >
          <FaVideo style={{ fontSize: 13, lineHeight: 1 }} />
        </a>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          title="Copy Meeting Link"
          onClick={() => handleCopyMeetingLink(link)}
          style={{ width: 32, height: 32, minWidth: 32, padding: 0, flex: "0 0 32px", display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}
        >
          <FaCopy style={{ fontSize: 12 }} />
        </button>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          title="Share Meeting Link"
          onClick={() => handleOpenShareModal(event)}
          style={{ width: 32, height: 32, minWidth: 32, padding: 0, flex: "0 0 32px", display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}
        >
          <FaShareAlt style={{ fontSize: 12 }} />
        </button>
        {deleteButton}
      </div>
    )
  }

  const listHeaderCellStyle = {
    background: "#f2f7fd",
    color: "#0b3f75",
    border: "1px solid #cfe0f2",
    fontSize: 12,
    fontWeight: 700,
    padding: "14px 16px",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  }

  const getSortValue = (ev, key) => {
    const valueMap = {
      recruiter: ev.recruiter,
      candidate: ev.candidate,
      job: ev.job,
      subject: ev.subject,
      subtype: ev.subtype,
      date: ev.selected_date,
      time: ev.selected_time,
      date_time: `${ev.selected_date || ""} ${ev.selected_time || ""}`,
      meeting_link: ev.meeting_link ? "Join" : "N/A",
      created_by_name: ev.created_by_name,
      event_type: ev.event_type,
      archived_by_name: ev.archived_by_name,
      deleted_by_name: ev.deleted_by_name,
      status: ev.is_deleted ? "Deleted" : ev.archived ? "Archived" : "",
      reason_to_archive: ev.reason_to_archive,
    }

    return valueMap[key] ?? ""
  }

  const handleSort = key => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }))
  }

  const SortHeader = ({ label, sortKey, align = "left" }) => (
    <th
      style={{
        ...listHeaderCellStyle,
        cursor: "pointer",
        userSelect: "none",
        textAlign: align,
      }}
      onClick={() => handleSort(sortKey)}
      title={`Sort by ${label}`}
    >
      <span className="d-inline-flex align-items-center gap-1">
        {label}
        <span style={{ color: sortConfig.key === sortKey ? "#1a5fa8" : "#8aa4bf", fontSize: 11 }}>
          {sortConfig.key === sortKey ? (sortConfig.direction === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </span>
    </th>
  )

  const listCellStyle = {
    background: "#fff",
    border: "1px solid #d8e4f0",
    padding: "14px 16px",
    verticalAlign: "middle",
    fontSize: 13,
  }

  const listFirstCellStyle = {
    ...listCellStyle,
    borderLeft: "1px solid #d8e4f0",
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
  }

  const listLastCellStyle = {
    ...listCellStyle,
    borderRight: "1px solid #d8e4f0",
    borderTopRightRadius: 8,
    borderBottomRightRadius: 8,
  }

  const pillStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    borderRadius: 999,
    padding: "5px 10px",
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: "nowrap",
  }

  const formatTimeRange = ev => {
    const startTime = formatTimeDirect(ev.selected_time || ev.start_time)
    const endTime = ev.end_time ? formatTimeDirect(ev.end_time) : ""

    return endTime && endTime !== "-" ? `${startTime} - ${endTime}` : startTime
  }

  const handleShowMoreEvents = (dayEvents, date) => {
    const earliestEvent = [...dayEvents].sort((a, b) => new Date(a.start) - new Date(b.start))[0]
    const targetDate = earliestEvent?.start ? new Date(earliestEvent.start) : new Date(date)

    setCalendarDate(new Date(date))
    setScrollToEventTime(targetDate)
    setCalendarView("week")
  }

  const listEvents = events
    .flatMap(event => event.fullData || [])
    .sort((a, b) => {
      const aValue = String(getSortValue(a, sortConfig.key)).toLowerCase()
      const bValue = String(getSortValue(b, sortConfig.key)).toLowerCase()
      const direction = sortConfig.direction === "asc" ? 1 : -1

      return aValue.localeCompare(bValue, undefined, { numeric: true }) * direction
    })

  const CalendarTableShimmer = () => {
    const columns = ["24%", "1fr", "1fr", "1fr", "1fr", "18%"]

    return (
      <div className="org-shimmer" aria-hidden="true">
        <div className="org-shimmer__table">
          <div
            className="org-shimmer__row org-shimmer__row--head"
            style={{ gridTemplateColumns: columns.join(" ") }}
          >
            {columns.map((_, index) => (
              <div key={`head-${index}`} className="org-shimmer__line org-shimmer__line--head" />
            ))}
          </div>
          {Array.from({ length: 7 }).map((_, rowIndex) => (
            <div
              key={`row-${rowIndex}`}
              className="org-shimmer__row"
              style={{ gridTemplateColumns: columns.join(" ") }}
            >
              {columns.map((_, columnIndex) => (
                <div
                  key={`cell-${rowIndex}-${columnIndex}`}
                  className={`org-shimmer__line ${columnIndex === columns.length - 1 ? "org-shimmer__line--short" : ""}`}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    )
  }

  const CustomToolbar = toolbar => {
    const viewLabels = [
      { key: "week", label: "Week" },
      { key: "month", label: "Month" },
    ]

    return (
      <div className="outlook-calendar-toolbar">
        <div className="outlook-calendar-title">
          <span className="outlook-calendar-title-icon"><FaCalendarDay /></span>
          <div>
            <div className="outlook-calendar-title-main">
              {calendarScope === "my"
                ? "My Interviews"
                : calendarScope === "select"
                  ? "Today Schedules"
                  : calendarScope === "archive"
                    ? "Archived Interviews"
                    : "All Interviews"}
            </div>
            <div
              className="outlook-calendar-title-sub"
              onClick={handleOpenCalendarDatePicker}
              title="Select date"
              style={{ cursor: "pointer" }}
            >
              {toolbar.label}
              <input
                ref={calendarDateInputRef}
                type="date"
                value={selectedFilterDate}
                onChange={handleCalendarDateChange}
                style={{
                  position: "absolute",
                  opacity: 0,
                  width: 1,
                  height: 1,
                  pointerEvents: "none",
                }}
                tabIndex={-1}
              />
            </div>
          </div>
        </div>
        <div className="outlook-calendar-nav">
          <button type="button" onClick={() => toolbar.onNavigate("PREV")} title="Previous">
            <FaChevronLeft />
          </button>
          <button type="button" onClick={() => toolbar.onNavigate("NEXT")} title="Next">
            <FaChevronRight />
          </button>
        </div>
        <div className="outlook-calendar-views">
          <button type="button" className="today" onClick={() => toolbar.onNavigate("TODAY")}>
            Today
          </button>
          {viewLabels.map(item => (
            <button
              type="button"
              key={item.key}
              className={toolbar.view === item.key ? "active" : ""}
              onClick={() => toolbar.onView(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
    )
  }
  const CalendarEvent = ({ event }) => {
    const items = event?.fullData || []
    const first = items[0] || {}
    const status = String(first.status || "").trim()
    const isCancelled = status.toLowerCase() === "cancelled" || String(first.subject || "").toLowerCase().includes("cancelled")
    const label = `${formatTimeDirect(first.selected_time)} ${status ? `${status}: ` : ""}${first.subject || "Interview"}; ${first.recruiter || "-"}`

    return (
      <div
        className={`outlook-calendar-event ${isCancelled ? "cancelled" : ""}`}
        title={label}
        onClick={() => {
          setModalDayEvents(items)
          setModalTitle("Interviews")
          setShowTodayModal(true)
        }}
      >
        <span className="outlook-calendar-event-time">{formatTimeDirect(first.selected_time)}</span>
        <span className="outlook-calendar-event-text">
          {first.subject || "Interview"}{items.length > 1 ? ` (+${items.length - 1})` : ""}
        </span>
      </div>
    )
  }
  const DateCellWrapper = ({ value, children }) => {
    const cellDate = moment(value).startOf("day")
    const todayDate = moment().startOf("day")

    const isToday = cellDate.isSame(todayDate, "day")

    return (
      <div className={`calendar-cell ${isToday ? "today-highlight" : ""}`}>
        {children}
      </div>
    )
  }

  const calendarSummaryRows = events.flatMap(event => event.fullData || [])
  const hasInterviewEnded = event => {
    const status = String(event.status || "").toLowerCase()
    if (["completed", "complete", "done"].includes(status)) return true
    const date = event.selected_date || event.start_date
    const endTime = event.end_time
    if (!date || !endTime) return false
    const [hours = 0, minutes = 0, seconds = 0] = String(endTime).split(":").map(Number)
    const endDate = new Date(`${date}T${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`)
    return !Number.isNaN(endDate.getTime()) && endDate <= summaryNow
  }
  const completedInterviewCount = calendarSummaryRows.filter(hasInterviewEnded).length
  const totalInterviewCount = calendarSummaryRows.length
  const upcomingInterviewCount = Math.max(0, totalInterviewCount - completedInterviewCount)

  return (
    <div className="container-fluid  outlook-calendar-page" style={{ padding: "32px" }}>
      <style>{`
        .outlook-calendar-page .table-responsive {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .outlook-calendar-page .table-responsive::-webkit-scrollbar {
          display: none;
          height: 0;
        }
        .outlook-calendar-page .outlook-company-filter {
          width: 100%;
        }
        .outlook-calendar-page .outlook-company-filter > div {
          min-width: 0 !important;
        }
        .outlook-calendar-page .outlook-company-filter > div:not(:first-child) {
          min-width: 0 !important;
        }
        .outlook-calendar-page .outlook-company-filter > div:not(:first-child) > .w-100,
        .outlook-calendar-page .outlook-company-filter > div:not(:first-child) > .form-control,
        .outlook-calendar-page .outlook-company-filter > div:not(:first-child) > .form-select,
        .outlook-calendar-page .outlook-company-filter > div:not(:first-child) .select__control {
          width: 100%;
          max-width: none !important;
        }
        @media (max-width: 1200px) {
          .outlook-calendar-page .outlook-company-filter {
            grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
          }
        }
        @media (max-width: 760px) {
          .outlook-calendar-page .outlook-company-filter {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }
        }
      `}</style>
      <div
        className="outlook-page-header"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
          background: "#eef6ff",
          border: "1px solid #cfe0f2",
          borderRadius: 10,
          padding: "16px 18px",
          boxShadow: "0 4px 14px rgba(26, 95, 168, 0.08)",
        }}
      >
        <div>
          <h5 className="outlook-page-title">My Calendar</h5>
          <p className="outlook-page-subtitle">Interview schedule with month, week, and day views.</p>
        </div>
        <div className="outlook-calendar-note" role="note">
          <FaInfoCircle aria-hidden="true" />
          <span>
            <strong>Meeting link note:</strong> Walk-in and Face-to-Face interviews do not generate meeting links.
          </span>
        </div>
        <div
          className="outlook-company-filter"
          style={{
            display: "grid",
            gridTemplateColumns: "86px minmax(270px, 1.8fr) minmax(145px, 1fr) minmax(190px, 1.1fr) 150px 150px 130px 140px 105px 130px",
            alignItems: "end",
            gap: 7,
            marginLeft: 0,
            width: "100%",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
            <span style={{ height: 22, display: "block" }} />
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                border: "1px solid #1a5fa8",
                borderRadius: 6,
                overflow: "hidden",
                height: 38,
                background: "#fff",
              }}
            >
              <button
                type="button"
                title="Calendar view"
                onClick={() => setDisplayMode("calendar")}
                style={{
                  width: 42,
                  height: "100%",
                  border: "none",
                  background: displayMode === "calendar" ? "#1a5fa8" : "#fff",
                  color: displayMode === "calendar" ? "#fff" : "#1a5fa8",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaCalendarDay />
              </button>
              <button
                type="button"
                title="List view"
                onClick={() => setDisplayMode("list")}
                style={{
                  width: 42,
                  height: "100%",
                  border: "none",
                  borderLeft: "1px solid #1a5fa8",
                  background: displayMode === "list" ? "#1a5fa8" : "#fff",
                  color: displayMode === "list" ? "#fff" : "#1a5fa8",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaListUl />
              </button>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", minWidth: 240, flex: "1 1 240px" }}>
            <label htmlFor="calendar-event-search" style={{ height: 22, marginBottom: 4, display: "flex", alignItems: "center", fontWeight: 500 }}>Search interviews</label>
            <div style={{ position: "relative" }}>
              <FaSearch
                aria-hidden="true"
                style={{ position: "absolute", left: 11, top: 12, color: "#6b7f93", fontSize: 13 }}
              />
              <input
                id="calendar-event-search"
                type="search"
                value={search}
                onChange={event => setSearch(event.target.value)}
                placeholder="Search name"
                aria-label="Search name"
                className="form-control"
                style={{ height: 38, paddingLeft: 32, borderColor: "#1a5fa8", fontSize: 13 }}
              />
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", minWidth: 250 }}>
            <label style={{ height: 22, marginBottom: 4, display: "flex", alignItems: "center", fontWeight: 500 }}>Job Order</label>
            <Select
              isLoading={jobLoading}
              options={jobOptions}
              value={selectedJob}
              onChange={value => setSelectedJob(value)}
              onInputChange={(value, actionMeta) => {
                if (actionMeta.action === "input-change" && value !== jobSearch) {
                  setJobSearch(value)
                  fetchJobOptions(1, value)
                }
                return value
              }}
              placeholder="Select"
              styles={jobSelectStyles}
              components={{ MenuList: JobMenuList }}
              isClearable
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", minWidth: 220 }}>
            <label style={{ height: 22, marginBottom: 4, display: "flex", alignItems: "center", fontWeight: 500 }}>Recruiter</label>
            <Select
              options={[{ value: "all", label: "All Recruiters" }, ...recruiterOptions]}
              value={selectedRecruiter || { value: "all", label: "All Recruiters" }}
              onChange={value => setSelectedRecruiter(value?.value === "all" ? null : value)}
              placeholder="Select recruiter"
              styles={jobSelectStyles}
              isClearable={false}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", minWidth: 150 }}>
            <label style={{ height: 22, marginBottom: 4, display: "flex", alignItems: "center", fontWeight: 500 }}>Start Date</label>
            <input
              type="date"
              className="form-control"
              value={startDate}
              max={endDate || undefined}
              onChange={event => {
                const value = event.target.value
                setStartDate(value)
                setDateRangeError(value && endDate && endDate < value ? "End date cannot be earlier than the start date." : "")
              }}
              style={{ height: 38, borderColor: "#1a5fa8" }}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", minWidth: 150 }}>
            <label style={{ height: 22, marginBottom: 4, display: "flex", alignItems: "center", fontWeight: 500 }}>End Date</label>
            <input
              type="date"
              className="form-control"
              value={endDate}
              min={startDate || undefined}
              onChange={event => {
                const value = event.target.value
                setEndDate(value)
                setDateRangeError(startDate && value && value < startDate ? "End date cannot be earlier than the start date." : "")
              }}
              style={{ height: 38, borderColor: "#1a5fa8" }}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", minWidth: 150 }}>
            <label style={{ height: 22, marginBottom: 4, display: "flex", alignItems: "center", fontWeight: 500 }}>View</label>
            <select
              className="form-select"
              value={calendarScope}
              onChange={e => setCalendarScope(e.target.value)}
              style={{ width: "100%", height: 38, color: "#1a5fa8", borderColor: "#1a5fa8" }}
            >
              <option value="select">Select (Today's Interviews)</option>
              <option value="all">All Interviews</option>
              <option value="my">My Interviews</option>
              <option value="archive">Archive</option>
            </select>
          </div>
          <div style={{ display: "flex", flexDirection: "column", minWidth: 150 }}>
            <label style={{ height: 22, marginBottom: 4, display: "flex", alignItems: "center", fontWeight: 500 }}>Select Company</label>
            <div className="w-100" style={{ maxWidth: "none", minWidth: 0 }}>
              <CompanySelector onCompanyChange={handleCompanyChange} selectedCompanyId={companyId} showAllOption />
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", minWidth: 118 }}>
            <span style={{ height: 22, display: "block" }} />
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={resetFilters}
              title="Reset all filters"
              style={{ height: 38, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}
            >
              <FaRedo size={12} /> Reset
            </button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", minWidth: 138 }}>
            <span style={{ height: 22, display: "block" }} />
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleExportExcel}
              disabled={exporting || !companyId}
              title="Export filtered interviews to Excel"
              style={{ height: 38, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}
            >
              <FaDownload size={12} /> {exporting ? "Exporting..." : "Export Excel"}
            </button>
          </div>
        </div>
        {dateRangeError && (
          <div style={{ color: "#b42318", fontSize: 12, marginTop: 8, width: "100%" }} role="alert">
            {dateRangeError}
          </div>
        )}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 14, margin: "16px 0" }}>
        {[
          ["Total Interviews", totalInterviewCount, "#1a5fa8", "#eef6ff"],
          ["Yet to Happen", upcomingInterviewCount, "#b26a00", "#fff7e8"],
          ["Completed", completedInterviewCount, "#16794a", "#eefaf3"],
        ].map(([label, value, color, background]) => (
          <div key={label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 82, padding: "14px 18px", background: "#fff", border: "1px solid #d9e7f5", borderTop: `3px solid ${color}`, borderRadius: 10, boxShadow: "0 6px 18px rgba(15, 95, 168, 0.06)" }}>
            <span style={{ color: "#52677d", fontSize: 13, fontWeight: 700 }}>{label}</span>
            <strong style={{ minWidth: 42, padding: "7px 10px", borderRadius: 8, background, color, fontSize: 22, textAlign: "center" }}>{value}</strong>
          </div>
        ))}
      </div>
      {loading ? (
        <div className="outlook-calendar-container">
          <CalendarTableShimmer />
        </div>
      ) : displayMode === "list" ? (
        <div
          className="outlook-calendar-container"
          style={{
            background: "#f7fbff",
            border: "1px solid #d9e7f5",
            borderRadius: 10,
            padding: 12,
            boxShadow: "0 8px 24px rgba(15, 95, 168, 0.08)",
          }}
        >
          <Table
            responsive={calendarScope !== "archive"}
            className={`mb-0 ${calendarScope === "archive" ? "calendar-archive-table" : "calendar-list-table"}`}
            style={{ borderCollapse: "separate", borderSpacing: "0 8px", width: "100%", tableLayout: "fixed" }}
          >
            <thead>
              {calendarScope === "archive" ? (
                <tr>
                  <SortHeader label="Job" sortKey="job" />
                  <SortHeader label="Subject" sortKey="subject" />
                  <SortHeader label="Created By" sortKey="created_by_name" />
                  <SortHeader label="Archived By" sortKey="archived_by_name" />
                  <SortHeader label="Deleted By" sortKey="deleted_by_name" />
                  <SortHeader label="Event Type" sortKey="event_type" />
                  <SortHeader label="Interview Date & Time" sortKey="date_time" />
                  <SortHeader label="Status" sortKey="status" />
                  <SortHeader label="Archive Reason" sortKey="reason_to_archive" />
                </tr>
              ) : (
                <tr>
                  <SortHeader label="Interviewer Name" sortKey="recruiter" />
                  <SortHeader label="Candidate Name" sortKey="candidate" />
                  <SortHeader label="Job Title" sortKey="job" />
                  <SortHeader label="Subject" sortKey="subject" />
                  <SortHeader label="Created By / At" sortKey="created_by_name" />
                  <SortHeader label="Subtype" sortKey="subtype" />
                  <SortHeader label="Interview Date & Time" sortKey="date_time" />
                  <th style={listHeaderCellStyle}>Meeting Link</th>
                  <th className="text-center" style={{ ...listHeaderCellStyle, minWidth: 0 }}>Actions</th>
                </tr>
              )}
            </thead>
            <tbody>
              {listEvents.length > 0 ? (
                listEvents.map((ev, index) => {
                  if (calendarScope === "archive") {
                    return (
                      <tr key={`${ev.selected_date}-${ev.selected_time}-${index}`}>
                        <td style={listFirstCellStyle} title={ev.job || "-"}>
                          <span className="calendar-archive-job-cell">
                            {ev.job || "-"}
                          </span>
                        </td>
                        <td style={listCellStyle}>{ev.subject}</td>
                        <td style={listCellStyle}>{ev.created_by_name}</td>
                        <td style={listCellStyle}>{ev.archived_by_name || "—"}</td>
                        <td style={listCellStyle}>{ev.is_deleted ? (ev.deleted_by_name || "—") : "—"}</td>
                        <td style={listCellStyle}>
                          <span style={{ ...pillStyle, background: "#eef6ff", color: "#1a5fa8" }}>
                            {ev.event_type}
                          </span>
                        </td>
                        <td style={listCellStyle}>
                          <div title={`${formatDateDirect(ev.selected_date)} ${formatTimeRange(ev)}`} style={{ display: "grid", gap: 5 }}>
                            <span style={{ ...pillStyle, background: "#fff7e8", color: "#9a5b00" }}>
                              {formatDateDirect(ev.selected_date)}
                            </span>
                            <span style={{ ...pillStyle, background: "#eefaf3", color: "#16794a" }}>
                              <FaClock />
                              {formatTimeRange(ev)}
                            </span>
                          </div>
                        </td>
                        <td style={listCellStyle}>
                          <span className={ev.is_deleted ? "deleted-badge" : "archived-badge"}>
                            {ev.is_deleted ? "Deleted" : ev.archived ? "Archived" : "—"}
                          </span>
                        </td>
                        <td style={listLastCellStyle}>
                          <div className="archive-reason" title={ev.reason_to_archive || "No reason provided"}>
                            <span>{ev.reason_to_archive || "No reason provided"}</span>
                          </div>
                        </td>
                      </tr>
                    )
                  }
                  return (
                    <tr key={`${ev.selected_date}-${ev.selected_time}-${index}`}>
                      <td style={listFirstCellStyle} title={ev.recruiter}>{ev.recruiter}</td>
                      <td style={listCellStyle}>
                        <span style={{ fontWeight: 700, color: "#18324d" }}>{ev.candidate}</span>
                      </td>
                      <td style={listCellStyle}>{ev.job}</td>
                      <td style={{ ...listCellStyle, minWidth: 260 }}>{ev.subject}</td>
                      <td style={{ ...listCellStyle, minWidth: 150 }}>
                        <div style={{ fontWeight: 700, color: "#18324d", fontSize: 13 }}>{ev.created_by_name}</div>
                        <div style={{ color: "#52677d", fontSize: 12, fontWeight: 500, marginTop: 4, whiteSpace: "nowrap" }}>{formatCreatedAt(ev.created_at)}</div>
                      </td>
                      <td style={listCellStyle}>
                        <span style={{ ...pillStyle, background: "#eef6ff", color: "#1a5fa8" }}>
                          {ev.subtype}
                        </span>
                      </td>
                      <td style={listCellStyle}>
                        <div title={`${formatDateDirect(ev.selected_date)} ${formatTimeRange(ev)}`} style={{ display: "grid", gap: 5 }}>
                          <span style={{ ...pillStyle, background: "#fff7e8", color: "#9a5b00" }}>
                            {formatDateDirect(ev.selected_date)}
                          </span>
                          <span style={{ ...pillStyle, background: "#eefaf3", color: "#16794a" }}>
                            <FaClock />
                            {formatTimeRange(ev)}
                          </span>
                        </div>
                      </td>
                      <td style={listCellStyle}>
                        <MeetingLinkActions event={ev} showDelete={false} />
                      </td>
                      <td style={{ ...listLastCellStyle, minWidth: 0, whiteSpace: "nowrap", padding: "14px 6px" }}>
                        <ActionButtons ev={ev} />
                      </td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td
                    colSpan={9}
                    style={{ padding: 0, background: "#fff", borderRadius: 8, border: "1px dashed #cfe0f2" }}
                  >
                    <EmptyInterviewState
                      message={debouncedSearch ? "No interviews found for this candidate or interview name." : undefined}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      ) : debouncedSearch && events.length === 0 ? (
        <div className="outlook-calendar-container">
          <EmptyInterviewState message="No interviews found for this candidate or interview name." />
        </div>
      ) : (
        <div className="calendar-container outlook-calendar-container">
          <Calendar
            key={`${calendarView}-${moment(calendarDate).format("YYYY-MM-DD")}-${scrollToEventTime.getHours()}-${scrollToEventTime.getMinutes()}`}
            localizer={localizer}
            events={events}
            date={calendarDate}
            view={calendarView}
            startAccessor="start"
            endAccessor="end"
            views={["month", "week"]}
            scrollToTime={scrollToEventTime}
            dayLayoutAlgorithm="no-overlap"
            onShowMore={handleShowMoreEvents}
            onView={view => setCalendarView(view)}
            onNavigate={date => setCalendarDate(date)}
            onSelectEvent={handleSelectEvent}
            onSelectSlot={handleSelectCalendarSlot}
            selectable
            components={{ toolbar: CustomToolbar, dateCellWrapper: DateCellWrapper, event: CalendarEvent }}
            eventPropGetter={event => {
              return {
                style: {
                  backgroundColor: "transparent",
                  border: "none",
                  boxShadow: "none",
                  padding: 0,
                },
              }
            }}
          />
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          marginTop: 16,
          padding: "14px 18px",
          background: "linear-gradient(135deg, #0f4c81 0%, #147d8a 100%)",
          border: "1px solid #0d6682",
          borderRadius: 10,
          boxShadow: "0 8px 20px rgba(15, 76, 129, 0.16)",
          color: "#fff",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 34,
              height: 34,
              borderRadius: 8,
              background: "rgba(255, 255, 255, 0.16)",
            }}
          >
            <FaCalendarDay size={16} />
          </span>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, opacity: 0.82 }}>
              Calendar Summary
            </div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>
              Total Interviews
            </div>
          </div>
        </div>
        <strong
          style={{
            minWidth: 56,
            padding: "6px 12px",
            borderRadius: 8,
            background: "#fff",
            color: "#0f4c81",
            fontSize: 22,
            lineHeight: 1.1,
            textAlign: "center",
          }}
        >
          {totalInterviewCount}
        </strong>
      </div>

      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        size="lg"
        centered
      >
        <Modal.Header
          closeButton
          style={{ background: "#f2f7fd", borderBottom: "1px solid #d9e7f5" }}
        >
          <Modal.Title style={{ color: "#0b3f75", fontSize: 20, fontWeight: 700 }}>
            Schedule Interview Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#f8fbff", padding: 18 }}>
          {editLoading ? (
            <div className="py-4">
              <CalendarTableShimmer />
            </div>
          ) : selectedInterview ? (
            <>
            <div>
              <div
                className="mb-3"
                style={{
                  background: "#fff",
                  border: "1px solid #d9e7f5",
                  borderRadius: 10,
                  padding: 14,
                  boxShadow: "0 6px 18px rgba(15, 95, 168, 0.06)",
                }}
              >
                <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                  <div>
                    <div style={{ color: "#6b7a90", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase" }}>
                      Subject
                    </div>
                    <div style={{ color: "#18324d", fontSize: 15, fontWeight: 700 }}>
                      {selectedInterview.subject || "-"}
                    </div>
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    <span style={{ ...pillStyle, background: "#eef6ff", color: "#1a5fa8", fontSize: 11, padding: "4px 9px" }}>
                      {selectedInterview.event_type || "-"}
                    </span>
                    <span style={{ ...pillStyle, background: "#eefaf3", color: "#16794a", fontSize: 11, padding: "4px 9px" }}>
                      {selectedInterview.status || "-"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="row g-3">
                {[
                  ["Subtype", selectedInterview.subtype || selectedInterview.event_sub_type || "-"],
                  ["Created At", formatCreatedAt(selectedInterview.created_at)],
                  ["Candidate", resolveCandidateName(selectedInterview, selectedInterview.candidate)],
                  ["Recruiter / Contact", selectedInterview.recruiter || "-"],
                  ["Job", selectedInterview.job || "-"],
                  ["Priority", selectedInterview.event_priority || "-"],
                  ["Reminder", selectedInterview.reminder || "-"],
                  ["Event timezone", selectedInterview.time_zone || "Asia/Kolkata"],
                  ["Candidate timezone", selectedInterview.candidate_timezone || "Asia/Kolkata"],
                  ["Recruiter timezone", selectedInterview.recruiter_timezone || "Asia/Kolkata"],
                ].map(([label, value]) => (
                  <div className={label === "Panel timezones" ? "col-12" : "col-md-6"} key={label}>
                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid #d9e7f5",
                        borderRadius: 8,
                        padding: "9px 11px",
                        minHeight: 60,
                      }}
                    >
                      <div style={{ color: "#6b7a90", fontSize: 11, fontWeight: 700 }}>{label}</div>
                      <div style={{ color: "#263442", fontSize: 13, fontWeight: 600, overflowWrap: "anywhere" }}>{value}</div>
                    </div>
                  </div>
                ))}

                <div className="col-md-4">
                  <div style={{ background: "#fff7e8", border: "1px solid #f3dfb8", borderRadius: 8, padding: "9px 11px" }}>
                    <div style={{ color: "#8a5a00", fontSize: 11, fontWeight: 700 }}>Start Date</div>
                    <input
                      type="date"
                      className="form-control form-control-sm mt-1"
                      value={selectedInterview.selected_date || selectedInterview.start_date || ""}
                      onChange={e =>
                        setSelectedInterview(prev => {
                          const next = {
                            ...prev,
                            selected_date: e.target.value,
                            start_date: e.target.value,
                          }
                          setEditTimeError(getEditTimeError(next))
                          return next
                        })
                      }
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div style={{ background: "#eefaf3", border: "1px solid #cfeedd", borderRadius: 8, padding: "9px 11px" }}>
                    <div className="d-flex justify-content-between align-items-center">
                      <div style={{ color: "#16794a", fontSize: 11, fontWeight: 700 }}>Start Time</div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#145c3a" }}>
                        {formatTimeDirect(selectedInterview.selected_time || selectedInterview.start_time)}
                      </div>
                    </div>
                    <input
                      type="time"
                      className="form-control form-control-sm mt-1"
                      value={String(selectedInterview.selected_time || selectedInterview.start_time || "").slice(0, 5)}
                      onChange={e =>
                        setSelectedInterview(prev => {
                          const next = {
                            ...prev,
                            selected_time: e.target.value,
                            start_time: e.target.value,
                          }
                          setEditTimeError(getEditTimeError(next))
                          return next
                        })
                      }
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div style={{ background: "#f5f7fb", border: "1px solid #dfe6ef", borderRadius: 8, padding: "9px 11px" }}>
                    <div className="d-flex justify-content-between align-items-center">
                      <div style={{ color: "#6b7a90", fontSize: 11, fontWeight: 700 }}>End Time</div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#263442" }}>
                        {formatTimeDirect(selectedInterview.end_time)}
                      </div>
                    </div>
                    <input
                      type="time"
                      className="form-control form-control-sm mt-1"
                      value={String(selectedInterview.end_time || "").slice(0, 5)}
                      onChange={e =>
                        setSelectedInterview(prev => {
                          const next = { ...prev, end_time: e.target.value }
                          setEditTimeError(getEditTimeError(next))
                          return next
                        })
                      }
                      style={{ borderColor: editTimeError ? "#dc3545" : undefined }}
                    />
                    {editTimeError && (
                      <div className="text-danger mt-1" style={{ fontSize: 11.5, fontWeight: 600 }}>
                        {editTimeError}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <section className="schedule-history-panel" aria-label="Schedule History">
              <div className="schedule-history-title">Schedule History</div>
              {scheduleHistoryLoading ? <div className="text-muted small py-3">Loading schedule history...</div> : scheduleHistoryError ? <div className="text-danger small py-3">{scheduleHistoryError}</div> : !scheduleHistory.length ? <div className="text-muted small py-3">No schedule history available.</div> : <div className="schedule-history-timeline">
                {scheduleHistory.map((entry, index) => {
                  const action = String(entry.action || "updated").toLowerCase()
                  const snapshot = getHistoryValue(entry.snapshot)
                  const next = getHistoryValue(entry.new_values)
                  const detailRows = getHistoryDetailRows(entry.snapshot, entry.new_values)
                  return <div className="schedule-history-item" key={`${entry.changed_at || index}-${index}`}>
                    <span className={`schedule-history-badge ${action === "deleted" ? "is-deleted" : ""}`}>{action === "deleted" ? "Deleted" : "Updated"}</span>
                    <div className="schedule-history-summary">
                      <div className="schedule-history-summary-row"><span>Candidate</span><strong>{snapshot.candidate_id_name || next.candidate_id_name || "--"}</strong></div>
                      <div className="schedule-history-summary-row"><span>Job</span><strong>{snapshot.job_id_name || next.job_id_name || "--"}</strong></div>
                      <div className="schedule-history-summary-row"><span>Company</span><strong>{snapshot.company_name || next.company_name || "--"}</strong></div>
                      <div className="schedule-history-summary-row"><span>Contact</span><strong>{snapshot.contact_id_name || next.contact_id_name || "--"}</strong></div>
                      <div className="schedule-history-summary-row"><span>Created by</span><strong>{snapshot.created_by_name || next.created_by_name || "--"}</strong></div>
                      <div className="schedule-history-summary-row"><span>Interview</span><strong>{snapshot.event_type || next.event_type || "--"}{(snapshot.event_sub_type || next.event_sub_type) ? ` · ${snapshot.event_sub_type || next.event_sub_type}` : ""}</strong></div>
                      <div className="schedule-history-summary-row"><span>Previous</span><strong>{formatHistoryDateTime(snapshot)}</strong></div>
                      {action !== "deleted" && <div className="schedule-history-summary-row is-new"><span>New</span><strong>{formatHistoryDateTime(next)}</strong></div>}
                      <div className="schedule-history-summary-row"><span>Duration</span><strong>{snapshot.duration || "--"}{action !== "deleted" && ` → ${next.duration || "--"}`}</strong></div>
                      <div className="schedule-history-summary-row"><span>Time zone</span><strong>{snapshot.time_zone || "--"}{action !== "deleted" && ` → ${next.time_zone || "--"}`}</strong></div>
                      <div className="schedule-history-summary-row full-width"><span>Subject</span><strong>{snapshot.subject || next.subject || "--"}</strong></div>
                    </div>
                    {action === "deleted" && <div><strong>Reason:</strong> {entry.deletion_reason || entry.reason || "--"}</div>}
                    <div><strong>{action === "deleted" ? "Deleted by" : "Changed by"}:</strong> {entry.changed_by_name || entry.updated_by_name || entry.changed_by || "--"}</div>
                    <div><strong>{action === "deleted" ? "Deleted at" : "Changed at"}:</strong> {formatHistoryTimestamp(entry.changed_at || entry.updated_at || entry.created_at)}</div>
                    {!!detailRows.length && <details className="schedule-history-details">
                      <summary>View full details</summary>
                      <div className="schedule-history-detail-grid">
                        <div className="schedule-history-detail-header">Field</div><div className="schedule-history-detail-header">Previous</div><div className="schedule-history-detail-header">New</div>
                        {detailRows.map(row => <React.Fragment key={row.label}><div>{row.label}</div><div>{String(row.before ?? "--")}</div><div>{String(row.after ?? "--")}</div></React.Fragment>)}
                      </div>
                    </details>}
                  </div>
                })}
              </div>}
            </section>
            </>
          ) : (
            <div className="text-center text-muted py-4">No interview details found.</div>
          )}
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button
            variant="secondary"
            className="blueBtn"
            style={{ fontSize: 13, padding: "6px 16px" }}
            onClick={() => setShowEditModal(false)}
            disabled={editSaving}
          >
            Close
          </Button>
          <Button
            className="blueBtn"
            style={{ fontSize: 13, padding: "6px 16px" }}
            onClick={handleUpdateInterview}
            disabled={
              editSaving ||
              editLoading ||
              !selectedInterview ||
              Boolean(editTimeError) ||
              !hasDateTimeChanged(selectedInterview)
            }
          >
            {editSaving ? "Saving..." : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showArchiveModal}
        onHide={() => {
          setShowArchiveModal(false)
          setArchiveReason("")
          setArchiveReasonErr("")
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Reason for Archive</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <label className="fw-semibold mb-2">
            Reason for Archive <span className="text-danger">*</span>
          </label>
          <textarea
            className="form-control"
            rows={4}
            placeholder="Enter the reason..."
            value={archiveReason}
            onChange={e => setArchiveReason(e.target.value)}
          />
          {archiveReasonErr && <small className="text-danger">{archiveReasonErr}</small>}
          <div className="d-flex justify-content-center gap-2 mt-3">
            <Button
              variant="secondary"
              className="blueBtn"
              onClick={() => {
                setShowArchiveModal(false)
                setArchiveReason("")
                setArchiveReasonErr("")
              }}
              disabled={archiveSaving}
            >
              Cancel
            </Button>
            <Button
              className="blueBtn"
              onClick={handleSaveArchive}
              disabled={archiveSaving}
            >
              {archiveSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => !deletingEventId && setShowDeleteModal(false)} centered>
        <Modal.Header closeButton={!deletingEventId}>
          <Modal.Title>Delete interview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">This interview will be archived and removed from the active calendar.</p>
          <label className="fw-semibold mb-2">Deletion reason <span className="text-danger">*</span></label>
          <textarea className="form-control" rows={4} value={deletionReason} placeholder="Enter the reason for deletion..." onChange={event => { setDeletionReason(event.target.value); setDeletionReasonErr("") }} />
          {deletionReasonErr && <small className="text-danger d-block mt-2">{deletionReasonErr}</small>}
          <div className="d-flex justify-content-center gap-2 mt-4">
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={Boolean(deletingEventId)}>Cancel</Button>
            <Button variant="danger" onClick={handleConfirmDelete} disabled={Boolean(deletingEventId) || !deletionReason.trim()}>{deletingEventId ? "Deleting..." : "Delete interview"}</Button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={showTodayModal}
        onHide={() => setShowTodayModal(false)}
        dialogClassName="calendar-interview-details-dialog"
        size="xl"
        centered
      >
        <Modal.Header
          closeButton
          style={{ background: "#f2f7fd", borderBottom: "1px solid #d9e7f5" }}
        >
          <Modal.Title
            className="d-flex align-items-center gap-2"
            style={{ color: "#0b3f75", fontSize: 18, fontWeight: 700 }}
          >
            <FaCalendarDay />
            {modalTitle}
            <span style={{ ...pillStyle, background: "#eef6ff", color: "#1a5fa8", fontSize: 11 }}>
              {modalDayEvents.length} {modalDayEvents.length === 1 ? "interview" : "interviews"}
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#f7fbff", padding: 16 }}>
          <div
            style={{
              background: "#fff",
              border: "1px solid #d9e7f5",
              borderRadius: 10,
              padding: 10,
              boxShadow: "0 8px 24px rgba(15, 95, 168, 0.06)",
            }}
          >
            <div className="calendar-interviews-table-scroll">
            <Table
              className="mb-0 calendar-interviews-table"
              style={{
                borderCollapse: "separate",
                borderSpacing: "0 8px",
                width: "100%",
                minWidth: 0,
                tableLayout: "fixed",
              }}
            >
              <thead>
                {calendarScope === "archive" ? (
                  <tr>
                    <th style={listHeaderCellStyle}>Candidate</th>
                    <th style={listHeaderCellStyle}>Job</th>
                    <th style={listHeaderCellStyle}>Subject</th>
                    <th style={listHeaderCellStyle}>Created By</th>
                    <th style={listHeaderCellStyle}>Archived By</th>
                    <th style={listHeaderCellStyle}>Deleted By</th>
                    <th style={listHeaderCellStyle}>Event Type</th>
                    <th style={listHeaderCellStyle}>Interview Date & Time</th>
                    <th style={listHeaderCellStyle}>Status</th>
                    <th style={listHeaderCellStyle}>Archive Reason</th>
                  </tr>
                ) : (
                  <tr>
                    <th style={listHeaderCellStyle}>Interviewer Name</th>
                    <th style={listHeaderCellStyle}>Candidate Name</th>
                    <th style={listHeaderCellStyle}>Subject</th>
                    <th style={listHeaderCellStyle}>Created By / At</th>
                    <th style={listHeaderCellStyle}>Subtype</th>
                    <th style={listHeaderCellStyle}>Interview Date & Time</th>
                    <th style={listHeaderCellStyle}>Meeting Link</th>
                    <th className="text-center" style={listHeaderCellStyle}>Actions</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {modalDayEvents.length > 0 ? (
                  modalDayEvents.map((ev, index) => {
                    if (calendarScope === "archive") {
                      return (
                        <tr key={index}>
                        <td style={listFirstCellStyle}>{ev.candidate}</td>
                        <td style={listCellStyle}>{ev.job}</td>
                          <td style={listCellStyle}>{ev.subject}</td>
                          <td style={listCellStyle}>{ev.created_by_name}</td>
                          <td style={listCellStyle}>{ev.archived_by_name || "—"}</td>
                          <td style={listCellStyle}>{ev.is_deleted ? (ev.deleted_by_name || "—") : "—"}</td>
                          <td style={listCellStyle}>
                          <span style={{ ...pillStyle, background: "#eef6ff", color: "#1a5fa8" }}>
                            {ev.event_type}
                            </span>
                          </td>
                          <td style={listCellStyle}>
                            <div title={`${formatDateDirect(ev.selected_date)} ${formatTimeRange(ev)}`} style={{ display: "grid", gap: 5 }}>
                              <span style={{ ...pillStyle, background: "#fff7e8", color: "#9a5b00" }}>
                                {formatDateDirect(ev.selected_date)}
                              </span>
                              <span style={{ ...pillStyle, background: "#eefaf3", color: "#16794a" }}>
                                <FaClock />
                                {formatTimeRange(ev)}
                              </span>
                            </div>
                          </td>
                          <td style={listCellStyle}>
                            <span className={ev.is_deleted ? "deleted-badge" : "archived-badge"}>
                              {ev.is_deleted ? "Deleted" : ev.archived ? "Archived" : "—"}
                            </span>
                          </td>
                        <td style={listLastCellStyle}>
                          <div className="archive-reason" title={ev.deletion_reason !== "-" ? ev.deletion_reason : ev.reason_to_archive}>
                            <span>{ev.deletion_reason !== "-" ? ev.deletion_reason : ev.reason_to_archive || "No reason provided"}</span>
                          </div>
                        </td>
                        </tr>
                      )
                    }

                    return (
                      <tr key={index}>
                        <td style={listFirstCellStyle} title={ev.recruiter}>{ev.recruiter}</td>
                        <td style={listCellStyle}>
                          <span style={{ fontWeight: 700, color: "#18324d" }}>{ev.candidate}</span>
                        </td>
                        <td style={{ ...listCellStyle, minWidth: 220 }}>{ev.subject}</td>
                        <td style={listCellStyle}>
                          <div style={{ fontWeight: 700, color: "#18324d", fontSize: 13 }}>{ev.created_by_name}</div>
                          <div style={{ color: "#52677d", fontSize: 12, fontWeight: 500, marginTop: 4, whiteSpace: "nowrap" }}>{formatCreatedAt(ev.created_at)}</div>
                        </td>
                        <td style={listCellStyle}>
                          <span style={{ ...pillStyle, background: "#eef6ff", color: "#1a5fa8" }}>
                            {ev.subtype}
                          </span>
                        </td>
                        <td style={listCellStyle}>
                          <div title={`${formatDateDirect(ev.selected_date)} ${formatTimeRange(ev)}`} style={{ display: "grid", gap: 5 }}>
                            <span style={{ ...pillStyle, background: "#fff7e8", color: "#9a5b00" }}>
                              {formatDateDirect(ev.selected_date)}
                            </span>
                            <span style={{ ...pillStyle, background: "#eefaf3", color: "#16794a" }}>
                              <FaClock />
                              {formatTimeRange(ev)}
                            </span>
                          </div>
                        </td>
                        <td style={listCellStyle}>
                          <MeetingLinkActions event={ev} />
                        </td>
                        <td style={listLastCellStyle}>
                          <ActionButtons ev={ev} />
                        </td>
                      </tr>
                    )
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={calendarScope === "archive" ? 6 : 8}
                      style={{ padding: 0, background: "#fff", borderRadius: 8, border: "1px dashed #cfe0f2" }}
                    >
                      <EmptyInterviewState compact />
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer
          className="justify-content-center"
          style={{ background: "#f7fbff", borderTop: "1px solid #d9e7f5" }}
        >
          <Button variant="secondary" className="blueBtn" onClick={() => setShowTodayModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showShareModal}
        onHide={() => setShowShareModal(false)}
        centered
        size="lg"
      >
        <Modal.Header
          closeButton
          style={{ background: "#f2f7fd", borderBottom: "1px solid #d9e7f5" }}
        >
          <Modal.Title style={{ color: "#0b3f75", fontSize: 18, fontWeight: 700 }}>
            Share Meeting Link
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#f8fbff", padding: 20 }}>
          {selectedEventForShare && (
            <div
              className="mb-3 d-flex align-items-center justify-content-between flex-wrap gap-2"
              style={{
                background: "#fff",
                border: "1px solid #d9e7f5",
                borderRadius: 10,
                padding: "10px 14px",
              }}
            >
              <div>
                <div style={{ color: "#6b7a90", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase" }}>
                  Subject
                </div>
                <div style={{ color: "#18324d", fontSize: 14, fontWeight: 700 }}>
                  {selectedEventForShare.subject || "Interview"}
                </div>
              </div>
              <span style={{ ...pillStyle, background: "#eefaf3", color: "#16794a" }}>
                <FaClock />
                {formatTimeRange(selectedEventForShare)}
              </span>
            </div>
          )}

          <div
            className="mb-3"
            style={{
              background: "#fff",
              border: "1px solid #d9e7f5",
              borderRadius: 10,
              padding: 14,
            }}
          >
            <div style={{ color: "#6b7a90", fontSize: 11, fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>
              Share With Company
            </div>
            <div style={{ maxWidth: 260 }}>
              <CompanySelector
                selectedCompanyId={shareCompanyId}
                onCompanyChange={handleShareCompanyChange}
              />
            </div>
            <div style={{ color: "#8a97a6", fontSize: 11, marginTop: 6 }}>
              Pick a company to load its recruiters/managers below — lets you share this link outside your own org too.
            </div>
          </div>

          <div className="row g-3">
            <div className="col-md-6">
              <div
                style={{
                  background: "#fff",
                  border: "1px solid #d9e7f5",
                  borderRadius: 10,
                  padding: 12,
                  height: "100%",
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={{ color: "#0b3f75", fontSize: 12.5, fontWeight: 700 }}>Recruiters / Managers</span>
                  {shareRecruiters.length > 0 && (
                    <span style={{ ...pillStyle, background: "#eef6ff", color: "#1a5fa8", fontSize: 11 }}>
                      {shareRecruiters.length} selected
                    </span>
                  )}
                </div>
                <input
                  type="search"
                  value={shareRecruiterSearch}
                  onChange={event => setShareRecruiterSearch(event.target.value)}
                  placeholder="Search recruiters..."
                  aria-label="Search recruiters and managers"
                  style={{
                    width: "100%",
                    height: 34,
                    marginBottom: 8,
                    padding: "6px 10px",
                    border: "1px solid #cbdbea",
                    borderRadius: 6,
                    fontSize: 12.5,
                    outline: "none",
                  }}
                />
                <div style={{ maxHeight: 200, overflowY: "auto" }}>
                  {shareRecruitersLoading ? (
                    <div className="text-muted" style={{ fontSize: 12.5 }}>Loading recruiters...</div>
                  ) : shareRecruiterOptions.filter(recruiter =>
                    recruiter.name.toLowerCase().includes(shareRecruiterSearch.trim().toLowerCase())
                  ).length ? (
                    shareRecruiterOptions
                      .filter(recruiter => recruiter.name.toLowerCase().includes(shareRecruiterSearch.trim().toLowerCase()))
                      .map(recruiter => (
                      <label
                        key={recruiter.id}
                        className="d-flex align-items-center gap-2 mb-1"
                        style={{
                          fontSize: 13,
                          padding: "6px 8px",
                          borderRadius: 6,
                          background: shareRecruiters.includes(recruiter.id) ? "#eef6ff" : "transparent",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={shareRecruiters.includes(recruiter.id)}
                          onChange={e => {
                            setShareRecruiters(prev =>
                              e.target.checked
                                ? [...prev, recruiter.id]
                                : prev.filter(id => id !== recruiter.id)
                            )
                          }}
                        />
                        <span
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: "50%",
                            background: "#1a5fa8",
                            color: "#fff",
                            fontSize: 10,
                            fontWeight: 700,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {recruiter.name?.[0]?.toUpperCase() || "?"}
                        </span>
                        {recruiter.name}
                      </label>
                    ))
                  ) : (
                    <div className="text-muted" style={{ fontSize: 12.5 }}>
                      {shareCompanyId
                        ? (shareRecruiterSearch ? "No matching recruiters found" : "No recruiters found")
                        : "Select a company to view recruiters"}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div
                style={{
                  background: "#fff",
                  border: "1px solid #d9e7f5",
                  borderRadius: 10,
                  padding: 12,
                  height: "100%",
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={{ color: "#0b3f75", fontSize: 12.5, fontWeight: 700 }}>Candidates</span>
                  {shareCandidates.length > 0 && (
                    <span style={{ ...pillStyle, background: "#eefaf3", color: "#16794a", fontSize: 11 }}>
                      {shareCandidates.length} selected
                    </span>
                  )}
                </div>
                <div style={{ maxHeight: 200, overflowY: "auto" }}>
                  {(selectedEventForShare?.candidate_ids || []).length ? (
                    selectedEventForShare.candidate_ids.map(candidate => (
                      <label
                        key={candidate.id}
                        className="d-flex align-items-center gap-2 mb-1"
                        style={{
                          fontSize: 13,
                          padding: "6px 8px",
                          borderRadius: 6,
                          background: shareCandidates.includes(candidate.id) ? "#eefaf3" : "transparent",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={shareCandidates.includes(candidate.id)}
                          onChange={e => {
                            setShareCandidates(prev =>
                              e.target.checked
                                ? [...prev, candidate.id]
                                : prev.filter(id => id !== candidate.id)
                            )
                          }}
                        />
                        <span
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: "50%",
                            background: "#16794a",
                            color: "#fff",
                            fontSize: 10,
                            fontWeight: 700,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {candidate.name?.[0]?.toUpperCase() || "?"}
                        </span>
                        {candidate.name}
                      </label>
                    ))
                  ) : (
                    <div className="text-muted" style={{ fontSize: 12.5 }}>No candidates found</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="justify-content-center" style={{ background: "#f8fbff", borderTop: "1px solid #d9e7f5" }}>
          <Button variant="secondary" className="blueBtn" onClick={() => setShowShareModal(false)}>Cancel</Button>
          <Button className="blueBtn" disabled={isSharing} onClick={handleSubmitShareMeeting}>
            {isSharing ? "Sharing..." : "Share"}
          </Button>
        </Modal.Footer>
      </Modal>

      <ScheduleInterviewDialog
        visible={scheduleInterviewOpen}
        onHide={() => setScheduleInterviewOpen(false)}
        companyId={companyId}
        disableCompany
        initialDate={scheduleInterviewDate}
        onScheduled={() => {
          setScheduleInterviewOpen(false)
          fetchEvents()
        }}
      />
    </div>
  )
}
export default MyCalendar
